import React, { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { Form, Input, Row, Col, message, Modal, Space } from "antd";
import type { BeneficiaryVO, PolicyholderVO } from "./orders";
import { COMMON_DICTS, getDictLabel, useMetadataStore } from "@ifa/utils";
import CustomButton from "../../components/CustomButton";
import CustomCard from "../../components/CustomCard";
import { TagSelector } from "../../components/TagSelector";
import { SelectInput } from "../../components/form/SelectInput";
import { useFormConfig } from "../../hooks/useFormConfig";
import { IdDocumentInput, type IdDocumentValue } from "../../components/form/IdDocumentInput";
import { BirthDateInput } from "../../components/form/BirthDateInput";
import { parseIdCard } from "../../utils/idCardParser";

interface BeneficiaryEditFormProps {
  initialData: Partial<BeneficiaryVO>;
  onSave: (data: BeneficiaryVO) => void;
  onCancel: () => void;
  existingBeneficiaries?: BeneficiaryVO[];
  isEdit?: boolean;
  index?: number;
  readOnly?: boolean;
  policyholder?: PolicyholderVO;
}

// Styles for the modal list
const cardStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e8e8e8",
  borderRadius: 12,
  padding: "16px 24px",
  marginBottom: 16,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  transition: "all 0.3s",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.02)",
  cursor: "pointer",
};

const nameStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: "#333",
  marginRight: 12,
};

const tagStyle: React.CSSProperties = {
  backgroundColor: "#fff7e6",
  color: "#fa8c16",
  border: "1px solid #ffd591",
  fontSize: 12,
  padding: "1px 8px",
  borderRadius: 4,
  lineHeight: 1.5,
};

const detailStyle: React.CSSProperties = {
  fontSize: 13,
  color: "#999",
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  marginTop: 8,
};

export interface BeneficiaryEditFormRef {
  validate: () => Promise<any>;
}

export const BeneficiaryEditForm = forwardRef<BeneficiaryEditFormRef, BeneficiaryEditFormProps>(
  ({ initialData, onSave, onCancel, existingBeneficiaries = [], readOnly = false, policyholder }, ref) => {
    const [form] = Form.useForm();

    useImperativeHandle(ref, () => ({
      validate: async () => {
        const errors = [];
        try {
          await form.validateFields();
        } catch (error: any) {
          if (error.errorFields) {
            errors.push(...error.errorFields);
          }
        }

        if (errors.length > 0) {
          if (errors[0].name) {
            form.scrollToField(errors[0].name);
          }
          throw { errorFields: errors };
        }
      },
    }));
    const [selectModalVisible, setSelectModalVisible] = useState(false);
    const { selectMetadataList, formRequiredConfig } = useMetadataStore();

    const [formData, setFormData] = useState<any>(initialData || {});
    const { isRequired, isVisible } = useFormConfig("BeneficiaryForm", formRequiredConfig, formData);

    const getOptions = (code: string, fallback: any[] = []) => {
      const meta = selectMetadataList.find((m) => m.code === code);
      if (meta?.items?.length) {
        return meta.items.map((i) => ({ label: i.name || i.key, value: i.key }));
      }
      return fallback;
    };

    const genderOptions = getOptions("GENDER", COMMON_DICTS.gender);
    const relationshipOptions = getOptions("RELATIONSHIP", COMMON_DICTS.relationship);

    const chineseSurname = Form.useWatch(["basic", "chineseSurname"], form);
    const chineseGivenName = Form.useWatch(["basic", "chineseGivenName"], form);
    const englishSurname = Form.useWatch(["basic", "englishSurname"], form);
    const englishGivenName = Form.useWatch(["basic", "englishGivenName"], form);
    const relationship = Form.useWatch("relationship", form);
    const idDocumentValue = Form.useWatch("_idDocumentValidator", form);

    useEffect(() => {
      if (initialData) {
        form.setFieldsValue({
          ...initialData,
          basic: {
            ...(initialData.basic || {}),
            birthDate: initialData.basic?.birthDate,
          },
          _idDocumentValidator: {
            idType: initialData.basic?.idType,
            idNumber: initialData.basic?.idNumber,
          },
          beneficiaryId: initialData.beneficiaryId,
          percentage: (initialData as any).percentage,
          remark: initialData.other?.remark ?? initialData.remark,
        });
      }
    }, [initialData, form]);

    const fillFromPolicyholder = useCallback((showSuccessMessage = true) => {
      const policyholderBasic = policyholder?.basic;

      if (!policyholderBasic) {
        message.warning("投保人資料為空，無法填充");
        return;
      }

      form.setFieldsValue({
        basic: {
          chineseSurname: policyholderBasic.chineseSurname,
          chineseGivenName: policyholderBasic.chineseGivenName,
          englishSurname: policyholderBasic.englishSurname,
          englishGivenName: policyholderBasic.englishGivenName,
          idType: policyholderBasic.idType,
          idNumber: policyholderBasic.idNumber,
          gender: policyholderBasic.gender,
          birthDate: policyholderBasic.birthDate,
        },
        _idDocumentValidator: {
          idType: policyholderBasic.idType,
          idNumber: policyholderBasic.idNumber,
        },
      });

      if (showSuccessMessage) {
        message.success("已自動填入投保人資料");
      }
    }, [form, policyholder]);

    // Auto-fill policyholder info when relationship is SELF
    useEffect(() => {
      if (relationship === "SELF" && policyholder) {
        fillFromPolicyholder(true);
      }
    }, [fillFromPolicyholder, policyholder, relationship]);

    const handleFinish = (values: any) => {
      const formattedValues = {
        ...values,
        basic: {
          ...values.basic,
          // Get idType and idNumber from _idDocumentValidator
          idType: values._idDocumentValidator?.idType,
          idNumber: values._idDocumentValidator?.idNumber,
        },
        beneficiaryId: initialData.beneficiaryId || undefined,
        other: {
          remark: values.remark,
        },
      };

      // Remove virtual field used for validation and root-level remark
      delete formattedValues._idDocumentValidator;
      delete formattedValues.remark;

      onSave(formattedValues);
    };

    const handleSelectExisting = (beneficiary: BeneficiaryVO) => {
      form.setFieldsValue({
        relationship: beneficiary.relationship,
        basic: {
          ...beneficiary.basic,
          birthDate: beneficiary.basic?.birthDate,
        },
        percentage: (beneficiary as any).percentage,
        _idDocumentValidator: {
          idType: beneficiary.basic?.idType,
          idNumber: beneficiary.basic?.idNumber,
        },
      });
      setSelectModalVisible(false);
      message.success("已填入選中受益人資料");
    };

    return createPortal(
      <div
        className="beneficiary-edit-form"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "#f5f5f5",
          zIndex: 99999,
          overflowY: "auto",
          padding: 0,
          boxSizing: "border-box",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "none",
        }}
      >
        <div
          className="page-header"
          style={{
            marginBottom: 24,
            zIndex: 900,
            display: "flex",
            alignItems: "center",
            background: "#FFFFFF",
            padding: "16px 24px",
            position: "sticky",
            top: 0,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <span style={{ fontSize: 14, color: "#666", cursor: "pointer" }} onClick={onCancel}>
            <span style={{ color: "#08C4C8" }}>訂單</span> &gt; <span style={{ color: "#08C4C8" }}>新增訂單/受保人資料</span> &gt;{" "}
            <span style={{ color: "#333", fontWeight: 500 }}>{readOnly ? "查看受益人" : "新增受益人"}</span>
          </span>
          <span style={{ marginLeft: "auto", fontSize: 20, color: "#999", cursor: "pointer" }} onClick={onCancel}>
            ✕
          </span>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          style={{ padding: "0 40px" }}
          disabled={readOnly}
          initialValues={{
            ...initialData,
            _idDocumentValidator: {
              idType: initialData.basic?.idType,
              idNumber: initialData.basic?.idNumber,
            },
          }}
          onValuesChange={(_, allValues) => setFormData(allValues)}
        >
          <CustomCard className="mb-4" bordered={false}>
            <Row gutter={24}>
              <Col span={12}>
                {isVisible("relationship") && (
                  <Form.Item label="與受保人關係" name="relationship" required={isRequired("relationship")} rules={[{ required: isRequired("relationship"), message: "請選擇與受保人關係" }]}>
                    <SelectInput options={relationshipOptions} value={relationship} onChange={(val) => form.setFieldsValue({ relationship: val })} title="與受保人關係" readOnly={readOnly} />
                  </Form.Item>
                )}
              </Col>
              {isVisible("percentage") && (
                <Col span={12}>
                  <Form.Item label="受益比例(%)" name="percentage" required={isRequired("percentage")} rules={[{ required: isRequired("percentage"), message: "請輸入受益比例" }]}>
                    <Input type="number" suffix="%" placeholder="請輸入" />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </CustomCard>

          <CustomCard
            title="受益人資料"
            className="mb-4"
            bordered={false}
            extra={
              !readOnly && (
                <Space size={12}>
                  {policyholder && relationship !== "SELF" && (
                    <CustomButton
                      height={32}
                      bgColor="#fff"
                      color="#00C7C7"
                      style={{ border: "1px solid #00C7C7" }}
                      onClick={() => fillFromPolicyholder(true)}
                    >
                      同投保人資料
                    </CustomButton>
                  )}
                  <CustomButton height={32} bgColor="#fff" color="#00C7C7" style={{ border: "1px solid #00C7C7" }} onClick={() => setSelectModalVisible(true)}>
                    選擇現有受益人
                  </CustomButton>
                </Space>
              )
            }
          >
            <Row gutter={24}>
              {relationship !== "SELF" && isVisible("basic.chineseSurname") && (
                <Col span={8}>
                  <Form.Item
                    label="姓氏(中文)"
                    name={["basic", "chineseSurname"]}
                    required={isRequired("basic.chineseSurname")}
                    rules={[{ required: isRequired("basic.chineseSurname"), message: "請輸入姓氏(中文)" }]}
                  >
                    <Input placeholder="請輸入" />
                  </Form.Item>
                </Col>
              )}
              {relationship !== "SELF" && isVisible("basic.chineseGivenName") && (
                <Col span={8}>
                  <Form.Item
                    label="名字(中文)"
                    name={["basic", "chineseGivenName"]}
                    required={isRequired("basic.chineseGivenName")}
                    rules={[{ required: isRequired("basic.chineseGivenName"), message: "請輸入名字(中文)" }]}
                  >
                    <Input placeholder="請輸入" />
                  </Form.Item>
                </Col>
              )}
              {relationship !== "SELF" && (
                <Col span={8}>
                  <Form.Item label="完整姓名(中文)">
                    <Input placeholder="請輸入" disabled value={(chineseSurname || "") + (chineseGivenName || "")} />
                  </Form.Item>
                </Col>
              )}
              {relationship !== "SELF" && isVisible("basic.englishSurname") && (
                <Col span={8}>
                  <Form.Item
                    label="姓氏(英文)"
                    name={["basic", "englishSurname"]}
                    required={isRequired("basic.englishSurname")}
                    rules={[
                      { required: isRequired("basic.englishSurname"), message: "請輸入姓氏(英文)" },
                      { pattern: /^[a-zA-Z\s]+$/, message: "只能輸入英文" },
                    ]}
                    normalize={(v) => v?.replace(/[^a-zA-Z\s]/g, "")}
                  >
                    <Input
                      inputMode="latin"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      autoComplete="off"
                      placeholder="請輸入"
                      onBlur={(e) => {
                        const val = e.target.value.replace(/[^a-zA-Z\s]/g, "").toUpperCase();
                        form.setFieldValue(["basic", "englishSurname"], val);
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
              {relationship !== "SELF" && isVisible("basic.englishGivenName") && (
                <Col span={8}>
                  <Form.Item
                    label="名字(英文)"
                    name={["basic", "englishGivenName"]}
                    required={isRequired("basic.englishGivenName")}
                    rules={[
                      { required: isRequired("basic.englishGivenName"), message: "請輸入名字(英文)" },
                      { pattern: /^[a-zA-Z\s]+$/, message: "只能輸入英文" },
                    ]}
                    normalize={(v) => v?.replace(/[^a-zA-Z\s]/g, "")}
                  >
                    <Input
                      inputMode="latin"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      autoComplete="off"
                      placeholder="請輸入"
                      onBlur={(e) => {
                        const val = e.target.value.replace(/[^a-zA-Z\s]/g, "").toUpperCase();
                        form.setFieldValue(["basic", "englishGivenName"], val);
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
              {relationship !== "SELF" && (
                <Col span={8}>
                  <Form.Item label="完整姓名(英文)">
                    <Input placeholder="請輸入" disabled value={(englishSurname || "") + " " + (englishGivenName || "")} />
                  </Form.Item>
                </Col>
              )}
              {relationship !== "SELF" && (isVisible("basic.idType") || isVisible("basic.idNumber")) && (
                <Col span={8}>
                  <Form.Item
                    label="身份證明文件號碼"
                    required={isRequired("basic.idType") || isRequired("basic.idNumber")}
                    name="_idDocumentValidator"
                    rules={[
                      {
                        validator: async (_, value) => {
                          if (isRequired("basic.idType") || isRequired("basic.idNumber")) {
                            if (!value) {
                              return Promise.reject(new Error("請完善身份證明文件資料"));
                            }
                            if (isRequired("basic.idType") && !value.idType) {
                              return Promise.reject(new Error("請選擇證件類型"));
                            }
                            if (isRequired("basic.idNumber") && !value.idNumber) {
                              return Promise.reject(new Error("請輸入證件號碼"));
                            }
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <IdDocumentInput
                      value={idDocumentValue}
                      onChange={(value: IdDocumentValue) => {
                        const currentBasic = form.getFieldValue("basic") || {};

                        // 更新身份证信息
                        form.setFieldsValue({
                          basic: {
                            ...currentBasic,
                            idType: value.idType,
                            idNumber: value.idNumber,
                          },
                          _idDocumentValidator: value,
                        });

                        // 如果身份证号码和类型都存在，立即解析并填充
                        if (value.idNumber && value.idType) {
                          const parseResult = parseIdCard(value.idNumber, value.idType);

                          if (parseResult.success) {
                            const basicUpdates: any = {};

                            // 只在字段为空时自动填充
                            if (parseResult.birthDate && !currentBasic.birthDate) {
                              basicUpdates.birthDate = parseResult.birthDate;
                            }

                            if (parseResult.gender && !currentBasic.gender) {
                              basicUpdates.gender = parseResult.gender;
                            }

                            // 如果有需要更新的字段，则更新表单
                            if (Object.keys(basicUpdates).length > 0) {
                              form.setFieldsValue({
                                basic: {
                                  ...currentBasic,
                                  idType: value.idType,
                                  idNumber: value.idNumber,
                                  ...basicUpdates,
                                },
                              });

                              // 显示提示信息
                              const messages = [];
                              if (basicUpdates.birthDate) messages.push("出生日期");
                              if (basicUpdates.gender) messages.push("性別");
                              if (messages.length > 0) {
                                message.success(`已自動填入${messages.join("、")}`);
                              }
                            }
                          }
                        }
                      }}
                      readOnly={readOnly}
                    />
                  </Form.Item>
                </Col>
              )}
              {relationship !== "SELF" && isVisible("basic.birthDate") && (
                <Col span={8}>
                  <Form.Item label="出生日期" name={["basic", "birthDate"]} required={isRequired("basic.birthDate")} rules={[{ required: isRequired("basic.birthDate"), message: "請選擇出生日期" }]}>
                    <BirthDateInput readOnly={readOnly} />
                  </Form.Item>
                </Col>
              )}

              {relationship !== "SELF" && isVisible("basic.gender") && (
                <Col span={8}>
                  <Form.Item label="性別" name={["basic", "gender"]} required={isRequired("basic.gender")} rules={[{ required: isRequired("basic.gender"), message: "請選擇性別" }]}>
                    <TagSelector options={genderOptions} disabled={readOnly} />
                  </Form.Item>
                </Col>
              )}
              {isVisible("other.remark") && (
                <Col span={24}>
                  <Form.Item label="備註/說明" name="remark" required={isRequired("other.remark")} rules={[{ required: isRequired("other.remark"), message: "請輸入備註/說明" }]}>
                    <Input placeholder="請輸入" />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </CustomCard>

          <div
            className="footer-actions"
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              padding: "16px 24px",
              background: "#fff",
              borderTop: "1px solid #eee",
              display: "flex",
              justifyContent: "space-between",
              zIndex: 100,
            }}
          >
            <CustomButton height={35} bgColor="#fff" color="#666" style={{ border: "1px solid #d9d9d9" }} onClick={onCancel}>
              返回上一頁
            </CustomButton>
            {!readOnly && (
              <CustomButton height={35} onClick={() => form.submit()}>
                確定
              </CustomButton>
            )}
          </div>
          {/* Spacer for fixed footer */}
          <div style={{ height: 80 }}></div>
        </Form>

        <Modal title="選擇現有受益人" open={selectModalVisible} onCancel={() => setSelectModalVisible(false)} footer={null} width={600}>
          <div style={{ maxHeight: 400, overflowY: "auto", padding: 4 }}>
            {existingBeneficiaries.map((b) => (
              <div
                key={b.beneficiaryId}
                onClick={() => handleSelectExisting(b)}
                style={cardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#00C7B6";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e8e8e8";
                  e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.02)";
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                    <span style={nameStyle}>
                      {b.basic?.chineseSurname}
                      {b.basic?.chineseGivenName} ({b.basic?.englishSurname} {b.basic?.englishGivenName})
                    </span>
                    <span style={tagStyle}>{getDictLabel(genderOptions, b.basic?.gender)}</span>
                  </div>
                  <div style={detailStyle}>
                    <span style={{ marginRight: 16, position: "relative" }}>
                      {b.basic?.idNumber}
                      <span style={{ position: "absolute", right: -9, top: 0, color: "#e8e8e8" }}>|</span>
                    </span>
                    <span style={{ marginLeft: 8 }}>{b.relationship}</span>
                  </div>
                </div>
                <CustomButton height={32} bgColor="#00C7C7" color="#fff" onClick={() => handleSelectExisting(b)}>
                  選擇
                </CustomButton>
              </div>
            ))}
            {existingBeneficiaries.length === 0 && <div className="text-center py-8 text-gray-400">暫無可選的現有受益人</div>}
          </div>
        </Modal>
      </div>,
      document.body,
    );
  },
);
